import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { bootstrapSession, authedFetch, registerSession, getLocationId, SinSesionGhl, type SessionState } from './lib/session.js';
import { RealtimeClient } from './lib/realtime.js';
import { modulesOf, type Snapshot } from './lib/snapshot-types.js';
import { claveGuardado, claveSeleccion, guardarSeleccion, leerSeleccionGuardada, paramsDelSnapshot, paramsDeLista, seleccionDeClave, type SeleccionPersonas } from './lib/filtro-panel.js';
import { SnapshotView } from './components/SnapshotView.js';
import { computePreset, type DateRangeValue } from './components/DateRangePicker.js';
import { CallDetail } from './components/CallDetail.js';
import { ReunionesGrabadas } from './components/ReunionesGrabadas.js';
import { CallsList } from './components/CallsList.js';
import { EmailsList } from './components/EmailsList.js';
import { WhatsAppList } from './components/WhatsAppList.js';
import { DealsList } from './components/DealsList.js';
import { MeetingsList } from './components/MeetingsList.js';
import { OpportunitiesList } from './components/OpportunitiesList.js';
import { LogMeetingButton } from './components/LogMeetingButton.js';
import { EntradaOperador } from './components/EntradaOperador.js';
import { HealthFooter } from './components/HealthFooter.js';
import { navigate, reemplazarHash } from './lib/hash-route.js';
import { type SnapshotTabValue } from './components/SnapshotTabs.js';
import { useHashRoute } from './lib/hash-route.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { conRecargaUnaVez } from './lib/carga-pantalla.js';
import { PestanasPanel, SeguimientoPestana, useSeguimiento } from './components/Seguimiento.js';
import { pedirConSesion } from './lib/seguimiento-client.js';

const DEFAULT_RANGE: DateRangeValue = (() => {
  const r = computePreset('30d');
  return { ...r, preset: '30d' };
})();

export type PantallaCompleta = 'feedback' | 'johnny' | 'onboarding' | 'alta' | 'clientes' | 'catalogo' | 'diagnostico' | null;

// PANTALLAS QUE SE CARGAN APARTE. El soporte, Johnny, el onboarding y la consola
// del operador (cartera, catalogo, diagnostico y alta) llevan cada uno su propio
// fichero de JavaScript: quien abre el panel de metricas no se descarga Johnny ni
// el alta, y al reves. El dashboard y sus listados se quedan en el fichero de
// entrada porque son la pantalla por defecto dentro de GHL.
// Una pagina abierta durante un despliegue se recarga una vez al pedir una pantalla
// cuyo fichero ya no existe (ver lib/carga-pantalla.ts).
const cargarPantalla = {
  feedback: conRecargaUnaVez(() => import('./components/FeedbackChat.js')),
  johnny: conRecargaUnaVez(() => import('./components/JohnnyChat.js')),
  onboarding: conRecargaUnaVez(() => import('./components/Onboarding.js')),
  consola: conRecargaUnaVez(() => import('./components/ConsolaOperador.js')),
};
const FeedbackChat = lazy(() => cargarPantalla.feedback().then((m) => ({ default: m.FeedbackChat })));
const JohnnyChat = lazy(() => cargarPantalla.johnny().then((m) => ({ default: m.JohnnyChat })));
const Onboarding = lazy(() => cargarPantalla.onboarding().then((m) => ({ default: m.Onboarding })));
const ConsolaOperador = lazy(() => cargarPantalla.consola().then((m) => ({ default: m.ConsolaOperador })));

/**
 * Pide el codigo de la pantalla completa en cuanto se sabe cual es, sin esperar
 * a la sesion: asi su descarga va en paralelo con el arranque y no despues.
 * Pedirlo dos veces no descarga nada dos veces (el navegador guarda el modulo).
 */
function precargarPantalla(pantalla: PantallaCompleta): void {
  if (pantalla === null) return;
  const cargar = pantalla === 'feedback' || pantalla === 'johnny' || pantalla === 'onboarding' ? cargarPantalla[pantalla] : cargarPantalla.consola;
  // Un fallo aqui se ignora a proposito: al pintarse, la pantalla vuelve a llamar
  // a import() y, si sigue fallando, lo recoge su ErrorBoundary.
  cargar().catch(() => undefined);
}

/** Lo que se ve mientras llega el codigo de una pantalla que se carga aparte. */
function CargandoPantalla() {
  return <p role="status" style={{ color: '#666' }}>Cargando…</p>;
}

/**
 * Que pantalla completa toca, si es que toca alguna.
 *
 * Hay DOS maneras de pedir una y las dos tienen que valer:
 *  - por la direccion del navegador (`/soporte`, `/onboarding`, `/alta`, o
 *    `?view=`), que es como GoHighLevel abre cada Custom Page en su iframe;
 *  - por la ruta de dentro de la app (`#/alta`, `#/feedback`...), que es lo que
 *    hacen los botones del propio panel.
 *
 * Johnny se escribe de dos formas a proposito: el producto lleva dos enes, pero
 * la Custom Page registrada en el Marketplace apunta a /johny y ese enlace vive
 * en la cuenta de un cliente, asi que cambiarlo es un paso manual de Joan. Se
 * aceptan las dos; parseHash hace lo mismo con la ruta de dentro.
 */
export function pantallaCompleta({ route, path, view, host }: {
  route: { kind: string }; path: string; view: string | null; host?: string;
}): PantallaCompleta {
  if (route.kind === 'diagnostico') return 'diagnostico';
  if (route.kind === 'clientes' || view === 'clientes' || path.startsWith('/clientes')) return 'clientes';
  if (route.kind === 'catalogo' || view === 'catalogo' || path.startsWith('/catalogo')) return 'catalogo';
  // La app del operador tiene cartera, catalogo y alta. Las metricas de cada
  // cliente conservan su origen y sus rutas propias.
  if (host && /^alta\./i.test(host)) {
    return route.kind === 'alta' || path.startsWith('/alta') || view === 'alta' ? 'alta' : 'clientes';
  }
  if (path.startsWith('/soporte') || view === 'feedback' || route.kind === 'feedback') return 'feedback';
  if (path.startsWith('/johnny') || path.startsWith('/johny') || view === 'johnny' || view === 'johny' || route.kind === 'johny') return 'johnny';
  if (path.startsWith('/onboarding') || view === 'onboarding' || route.kind === 'onboarding') return 'onboarding';
  if (path.startsWith('/alta') || view === 'alta' || route.kind === 'alta') return 'alta';
  return null;
}

/** Para quien es un snapshot: el cliente y el usuario de la sesion (el token solo la autoriza). */
export function identidadSesion(s: SessionState): string {
  return JSON.stringify([s.client.id, s.user.id]);
}

/** De que es un snapshot: identidad, personas, fechas y pestana. Una respuesta de otra clave no se pinta. */
function claveSnapshot(s: SessionState, personas: string, range: DateRangeValue, tab: SnapshotTabValue): string {
  return JSON.stringify([identidadSesion(s), personas, range.from, range.to, range.preset, tab]);
}

/** Donde se recuerda lo elegido en el desplegable de personas: por inquilino y por usuario. */
function guardadoDe(s: SessionState): string {
  return claveGuardado(s.client.id, s.user.id);
}

export function App() {
  const [state, setState] = useState<{ status: 'loading' | 'ready' | 'error' | 'login'; session?: SessionState; error?: string }>({ status: 'loading' });
  // El snapshot guarda de quien es: el de otra identidad no se pinta nunca (ver `snapshot` abajo).
  const [cargado, setCargado] = useState<{ identidad: string; datos: Snapshot } | null>(null);
  // LAS PERSONAS QUE SE VEN (el desplegable; sustituye a "Yo / Equipo"). Por defecto, todas.
  // Se guarda como texto ('todas' o ids ordenados) para que no cambie de identidad en cada
  // render, y de quien es: lo elegido por un usuario no se aplica a otro. Sin eleccion en
  // esta sesion, lo que este usuario dejo guardado en este navegador (o "todas").
  const [eleccion, setEleccion] = useState<{ identidad: string; personas: string } | null>(null);
  const [range, setRange] = useState<DateRangeValue>(DEFAULT_RANGE);
  // Quarter tabs removed 2026-05-25: the date picker covers the same use case
  // without dual UI; frozen snapshots can be re-introduced later as a menu item.
  const [tab] = useState<SnapshotTabValue>({ kind: 'current' });
  const [streamStatus, setStreamStatus] = useState<'connecting' | 'open' | 'closed'>('closed');
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // LA RUTA SE ESCUCHA AQUI, en el componente que decide que pantalla se pinta.
  // Antes esta funcion leia `window.location.hash` a pelo en mitad del render y
  // el unico que escuchaba los cambios era RouteOverlay, que vive DENTRO del
  // dashboard y no sabe nada del alta: pulsar "Alta" cambiaba la direccion y no
  // pasaba nada de nada, hasta que otra cosa cualquiera (un evento del stream, el
  // refresco de los 30 s) provocaba un render y entonces si aparecia. Tiene que
  // ir antes de los `return` de abajo: los hooks se llaman siempre, o React
  // revienta cuando cambia la pantalla.
  const route = useHashRoute();
  const pedida = typeof window === 'undefined' ? null : pantallaCompleta({
    route, path: window.location.pathname,
    view: new URLSearchParams(window.location.search).get('view'), host: window.location.hostname,
  });
  const pantallaRef = useRef(pedida);
  pantallaRef.current = pedida;
  // RESPUESTAS VIEJAS DEL SNAPSHOT. Se pide desde tres sitios (al cambiar personas o
  // fechas, el refresco de 30 s y el del stream) y las respuestas llegan en
  // cualquier orden. Cada peticion lleva su numero y su clave (identidad, personas,
  // fechas, pestana): solo se aplica si su clave es la de ahora y si es mas nueva
  // que la ultima aplicada. Asi una lenta de unas personas no pisa la de otras, una de
  // otra sesion no se pinta, y lo pintado nunca va hacia atras. No se exige que sea
  // la ultima PEDIDA: con eventos seguidos del stream, cada respuesta quedaria
  // tirada por la siguiente peticion y el panel no se refrescaria nunca.
  const identidad = state.session ? identidadSesion(state.session) : null;
  const personas: string = !state.session ? 'todas'
    : eleccion && eleccion.identidad === identidad ? eleccion.personas
    : leerSeleccionGuardada(guardadoDe(state.session));
  const claveVigente = useRef<string | null>(null);
  claveVigente.current = state.session ? claveSnapshot(state.session, personas, range, tab) : null;
  const ultimaPedida = useRef(0);
  const ultimaAplicada = useRef(0);
  const enVuelo = useRef<{ clave: string; control: AbortController } | null>(null);
  // EL ERROR DEL SNAPSHOT ES DEL PANEL, NO DE LA SESION: se guarda aparte (de quien es) para que
  // no tape la pestaña «Seguimiento». En el panel se ve igual que antes ("No se pudo cargar el
  // panel") y deja de pedir; al irse a otra pestaña se olvida, y al volver se pide otra vez.
  const [errorPanel, setErrorPanel] = useState<{ identidad: string; mensaje: string } | null>(null);
  const errorDelPanel = errorPanel && errorPanel.identidad === identidad ? errorPanel.mensaje : null;
  // SEGUIMIENTO: una pestaña propia del dashboard (#/seguimiento), no un bloque del panel.
  // El servidor dice si este inquilino la tiene (solo fluxo); si no, una respuesta y el
  // panel queda exactamente como siempre, sin pestañas. Con la pestaña abierta su vista
  // (#/seguimiento/<vista>) se mantiene al dia y el snapshot no se pide (no hace falta para
  // ella, y un fallo suyo no la tapa): se pide al volver al panel. Las derivaciones ya no
  // tienen pestaña (revamp del 6-oct, noche): se hacen solas y salen como avisos.
  const enDashboard = state.status === 'ready' && pedida === null && state.session ? state.session : null;
  const vistaSeguimiento = route.kind === 'seguimiento' ? route.vista ?? 'secuencia' : 'secuencia';
  const seguimiento = useSeguimiento(
    enDashboard ? pedirConSesion(enDashboard) : null, enDashboard ? identidadSesion(enDashboard) : null,
    pedida === null && route.kind === 'seguimiento',
  );
  const enSeguimiento = pedida === null && route.kind === 'seguimiento' && seguimiento.datos?.encendido !== false;
  // Un enlace a una vista que ya no existe (hoy, recibidos, cifras, derivaciones; desde el 20-oct, borradores
  // y whatsapp) se queda en #/seguimiento, que es la «Secuencia».
  const rutaAntigua = route.kind === 'seguimiento' && route.antigua === true;
  useEffect(() => { if (rutaAntigua) reemplazarHash('#/seguimiento'); }, [rutaAntigua]);
  const enPanel = pedida === null && !enSeguimiento;
  const enPanelRef = useRef(enPanel);
  enPanelRef.current = enPanel;

  useEffect(() => {
    // La pantalla pedida empieza a descargarse ya, mientras se resuelve la sesion.
    precargarPantalla(pantallaRef.current);
    void (async () => {
      try {
        const session = await bootstrapSession();
        registerSession(session, (fresh) => setState({ status: 'ready', session: fresh }));
        setState({ status: 'ready', session });
      } catch (err) {
        // Sin via de GHL no hay nada roto: se pide la contrasena de operador.
        if (err instanceof SinSesionGhl) { setState({ status: 'login' }); return; }
        setState({ status: 'error', error: err instanceof Error ? err.message : String(err) });
      }
    })();
  }, []);

  const entrar = (session: SessionState): void => {
    registerSession(session, (fresh) => setState({ status: 'ready', session: fresh }));
    setState({ status: 'ready', session });
  };

  const fetchSnapshot = async (session: SessionState, currentPersonas: string, currentRange: DateRangeValue, currentTab: SnapshotTabValue): Promise<void> => {
    const n = ++ultimaPedida.current;
    const clave = claveSnapshot(session, currentPersonas, currentRange, currentTab);
    // Lo pedido para otra clave ya no sirve: se corta (la comprobacion de abajo es la que manda).
    if (enVuelo.current && enVuelo.current.clave !== clave) enVuelo.current.control.abort();
    const control = new AbortController();
    enVuelo.current = { clave, control };
    const deAhora = () => clave === claveVigente.current;
    try {
      // El periodo lo resuelve el servidor en cada peticion (en la zona del
      // inquilino): se manda el preset; las fechas solo cuentan si es a medida.
      const q = currentTab.kind === 'historic' && currentTab.year && currentTab.quarter
        ? new URLSearchParams({ scope: 'team', year: String(currentTab.year), quarter: String(currentTab.quarter) })
        : paramsDelSnapshot(seleccionDeClave(currentPersonas), currentRange);
      const res = await authedFetch(session, `/api/dashboard/snapshot?${q.toString()}`, { signal: control.signal });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(`snapshot_${res.status}: ${JSON.stringify(j)}`);
      }
      const datos = (await res.json()) as Snapshot;
      if (!deAhora() || n <= ultimaAplicada.current) return;
      ultimaAplicada.current = n;
      setCargado({ identidad: identidadSesion(session), datos });
    } catch (err) {
      // El error de una peticion vieja (u otra clave, o cortada) no tumba el panel: solo el de la ultima pedida.
      if (!deAhora() || n !== ultimaPedida.current) return;
      if (enPanelRef.current) setErrorPanel({ identidad: identidadSesion(session), mensaje: err instanceof Error ? err.message : String(err) });
    } finally {
      if (enVuelo.current?.control === control) enVuelo.current = null;
    }
  };

  // Fuera del panel, su error se olvida: al volver se pide otra vez.
  useEffect(() => {
    if (!enPanel && errorPanel) setErrorPanel(null);
  }, [enPanel, errorPanel]);

  const panelVivo = enPanel && !errorDelPanel;

  useEffect(() => {
    if (state.status !== 'ready' || !panelVivo) return;
    const session = state.session!;
    void fetchSnapshot(session, personas, range, tab);
  }, [state.status, identidad, personas, range, tab, panelVivo]);

  // Auto-refresh fallback: if SSE has been down for >30s, poll snapshot every 30s
  // (only for current view, never historic). Stops when SSE recovers.
  useEffect(() => {
    if (state.status !== 'ready' || !panelVivo) return;
    if (tab.kind !== 'current') return;
    if (streamStatus === 'open') {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
      return;
    }
    const session = state.session!;
    pollTimer.current = setInterval(() => {
      void fetchSnapshot(session, personas, range, tab);
    }, 30_000);
    return () => {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    };
  }, [state.status, identidad, streamStatus, tab, personas, range, panelVivo]);

  // Real-time SSE connection: any GHL event triggers a debounced refetch.
  useEffect(() => {
    if (state.status !== 'ready' || !panelVivo) return;
    const session = state.session!;
    const client = new RealtimeClient({
      getToken: async () => {
        let s = session;
        if (Date.now() + 30_000 > session.expiresAt) {
          s = await bootstrapSession();
          // Como al arrancar: authedFetch pide con la sesion registrada, y tiene que ser
          // esta. Si no, se pediria con la de antes y se pintaria como de esta.
          registerSession(s, (fresh) => setState({ status: 'ready', session: fresh }));
          setState({ status: 'ready', session: s });
        }
        // A la URL del stream va un ticket de 60 s, no la sesion (que acabaria en logs).
        const r = await authedFetch(s, '/api/dashboard/stream-ticket', { method: 'POST' });
        if (!r.ok) throw new Error(`stream-ticket ${r.status}`);
        return ((await r.json()) as { ticket: string }).ticket;
      },
      onEvent: () => {
        if (tab.kind !== 'current') return; // historic snapshots are immutable
        if (refetchTimer.current) clearTimeout(refetchTimer.current);
        refetchTimer.current = setTimeout(() => {
          void fetchSnapshot(session, personas, range, tab);
        }, 800);
      },
      onStatusChange: setStreamStatus,
    });
    void client.connect();
    return () => {
      client.close();
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
    };
  }, [state.status, identidad, personas, range, tab, panelVivo]);

  if (state.status === 'loading') {
    return <Page><h1 style={{ color: '#666' }}>Cargando el panel de Fluxo…</h1></Page>;
  }
  if (state.status === 'login') {
    return <EntradaOperador onEntra={entrar} />;
  }
  if (state.status === 'error') {
    return <PaginaDeError error={state.error} />;
  }

  const session = state.session!;
  // Solo el snapshot de ESTA identidad: tras cambiar de sesion, el de la anterior no se pinta.
  const snapshot = cargado && cargado.identidad === identidadSesion(session) ? cargado.datos : null;

  // PANTALLA COMPLETA: se pinta SOLO la vista pedida, sin el marco del dashboard.
  // La decision vive en `pantallaCompleta` (abajo, y con tests): aqui solo se
  // pinta lo que diga. El `route` sale del hook de arriba, asi que cambiar de
  // ruta repinta esto; leer `window.location.hash` a pelo en mitad del render
  // es justo lo que hacia que pulsar "Alta" no hiciera nada.
  if (pedida === 'feedback') {
    const chatId = route.kind === 'feedback' ? route.chatId : /^\/soporte\/([^/?]+)/.exec(window.location.pathname)?.[1];
    return (
      <Page>
        <ErrorBoundary label="FeedbackChat"><Suspense fallback={<CargandoPantalla />}><FeedbackChat session={session} chatId={chatId} embed /></Suspense></ErrorBoundary>
      </Page>
    );
  }
  if (pedida === 'johnny') {
    const chatId = route.kind === 'johny' ? route.chatId : /^\/johnn?y\/([^/?]+)/.exec(window.location.pathname)?.[1];
    return (
      <Page>
        <ErrorBoundary label="JohnnyChat"><Suspense fallback={<CargandoPantalla />}><JohnnyChat session={session} chatId={chatId} embed /></Suspense></ErrorBoundary>
      </Page>
    );
  }
  if (pedida === 'onboarding') {
    return (
      <Page>
        <ErrorBoundary label="Onboarding"><Suspense fallback={<CargandoPantalla />}><Onboarding session={session} embed /></Suspense></ErrorBoundary>
      </Page>
    );
  }
  if (pedida === 'alta' || pedida === 'clientes' || pedida === 'catalogo' || pedida === 'diagnostico') {
    return (
      <ErrorBoundary label="Consola de clientes"><Suspense fallback={<Page><CargandoPantalla /></Page>}><ConsolaOperador key={`${session.client.id}:${session.user.id}`} session={session} pantalla={pedida} abrirSlug={route.kind === 'alta' ? route.slug : undefined} diagnosticoAlta={route.kind === 'alta' ? route.diagnostico : undefined} diagnosticoId={route.kind === 'diagnostico' ? route.id : undefined} /></Suspense></ErrorBoundary>
    );
  }

  // LAS REUNIONES GRABADAS (Fluxo para Meet): su propia página, sin el marco del panel y sin
  // esperar al snapshot (27-oct, «en un entorno como el de Granola»). Solo las tiene Fluxo.
  if ((route.kind === 'reuniones-grabadas' || route.kind === 'reunion') && session.client.slug === 'fluxo') {
    return (
      <ErrorBoundary label="Reuniones grabadas">
        <ReunionesGrabadas key="reuniones" session={session} id={route.kind === 'reunion' ? route.id : null} />
      </ErrorBoundary>
    );
  }

  // LA PESTAÑA «SEGUIMIENTO»: no espera al snapshot (no lo usa). Mientras no se sabe si
  // este inquilino la tiene, «Cargando…»; si no la tiene, se ve el panel de siempre.
  const pestanas = seguimiento.datos?.encendido
    ? <PestanasPanel activa={enSeguimiento ? 'seguimiento' : 'panel'} />
    : null;
  if (enSeguimiento) {
    return (
      <Page>
        {pestanas}
        <ErrorBoundary label="Seguimiento"><SeguimientoPestana uso={seguimiento} vista={vistaSeguimiento} /></ErrorBoundary>
      </Page>
    );
  }

  // El snapshot SOLO hace falta para el dashboard, y por eso este guard esta
  // aqui abajo y no arriba: el alta, el soporte, el onboarding y Johnny no
  // tienen nada que ver con los datos de ventas. Estando arriba, un snapshot
  // lento o roto dejaba la pantalla de alta en "Cargando datos..." sin poder
  // hacer nada, y el servidor de demo tenia que inventarse uno falso para que
  // se pudiera ver el alta. Con las pestañas encima (donde las hay): mientras
  // carga, o si falla, «Seguimiento» se puede abrir igual.
  if (errorDelPanel) {
    return <PaginaDeError error={errorDelPanel} pestanas={pestanas} />;
  }
  if (!snapshot) {
    return <Page>{pestanas}<p style={{ color: '#666' }}>Cargando datos…</p></Page>;
  }

  // Todas las personas pueden ver a todas (AT y Fluxo lo quieren asi: el equipo entero
  // mira las mismas cifras). Lo que se elige se recuerda por quien mira.
  const elegirPersonas = (sel: SeleccionPersonas): void => {
    const clave = claveSeleccion(sel);
    setEleccion({ identidad: identidadSesion(session), personas: clave });
    guardarSeleccion(guardadoDe(session), sel);
  };
  return (
    <Page>
      {pestanas}
      <BarraPanel session={session} snapshot={snapshot} />
      <SnapshotView
        {...(session.client.slug === 'fluxo' ? { session } : {})}
        snapshot={snapshot}
        user={session.user}
        personas={seleccionDeClave(personas)}
        onPersonasChange={elegirPersonas}
        range={range}
        onRangeChange={setRange}
        streamStatus={streamStatus}
      />
      <RouteOverlay session={session} personas={personas} range={range} snapshot={snapshot} />
      <HealthFooter session={session} />
    </Page>
  );
}

/**
 * La barra de arriba del panel, solo si hay algo que poner: el acceso del operador,
 * en Fluxo sus reuniones grabadas con Fluxo para Meet (27-oct, Joan: «poder acceder desde
 * ahí con un botón») y, con Zoho, «Registrar reunión». Sin Zoho el boton escribia en la
 * nada: las reuniones salen de las citas de GHL (ver modulesOf).
 */
export function BarraPanel({ session, snapshot }: { session: SessionState; snapshot: Snapshot }) {
  const operador = session.user.operator === true;
  const grabadas = session.client.slug === 'fluxo';
  const registrarReunion = modulesOf(snapshot).registrar_reunion;
  if (!operador && !grabadas && !registrarReunion) return null;
  return (
    <div className="panel-barra">
      {grabadas && <a href="#/reuniones-grabadas" className="panel-barra-enlace" title="Las reuniones grabadas con Fluxo para Meet y su transcripción">Reuniones grabadas</a>}
      {operador && <a href="#/clientes" className="panel-barra-enlace" title="Clientes y altas (solo operador)">Clientes</a>}
      {registrarReunion && <LogMeetingButton session={session} />}
    </div>
  );
}

function RouteOverlay({ session, personas, range, snapshot }: {
  session: SessionState; personas: string; range: DateRangeValue; snapshot: Snapshot;
}) {
  const route = useHashRoute();
  // GHL location_id for building "open in GHL" links. Primary source is the
  // session (returned by the server exchange) because it survives in-app hash
  // navigation; getLocationId() is a URL fallback for the legacy CML path.
  const locationId = session.locationId ?? getLocationId();
  // Zoho data center: hardcoded for AT (.eu); future: read from integration config.
  const zohoDataCenter = 'eu';
  // Las listas piden el filtro que aplico el snapshot pintado: las mismas filas que suma la tarjeta.
  const filtro = paramsDeLista(snapshot, seleccionDeClave(personas), range);

  if (route.kind === 'call') {
    return <ErrorBoundary label="CallDetail"><CallDetail session={session} callId={route.id} locationId={locationId} /></ErrorBoundary>;
  }
  if (route.kind === 'calls-list') {
    return (
      <ErrorBoundary label={`CallsList · ${route.disposition}`}>
        <CallsList session={session} disposition={route.disposition} categoria={route.categoria} filtro={filtro} />
      </ErrorBoundary>
    );
  }
  if (route.kind === 'emails-list') {
    return (
      <ErrorBoundary label="EmailsList">
        <EmailsList session={session} scope={route.scope} filtro={filtro} locationId={locationId} filter={route.filter ?? 'all'} />
      </ErrorBoundary>
    );
  }
  if (route.kind === 'whatsapp-list') {
    return (
      <ErrorBoundary label="WhatsAppList">
        <WhatsAppList session={session} scope={route.scope} filtro={filtro} locationId={locationId} filter={route.filter ?? 'all'} />
      </ErrorBoundary>
    );
  }
  if (route.kind === 'won-deals') {
    return (
      <ErrorBoundary label="DealsList">
        <DealsList session={session} scope={route.scope} from={snapshot.filtro?.desde ?? range.from} to={snapshot.filtro?.hasta ?? range.to} zohoDataCenter={zohoDataCenter} />
      </ErrorBoundary>
    );
  }
  if (route.kind === 'meetings-list') {
    return (
      <ErrorBoundary label={`MeetingsList · ${route.bucket}`}>
        <MeetingsList bucket={route.bucket} snapshot={snapshot} locationId={locationId} zohoDataCenter={zohoDataCenter} />
      </ErrorBoundary>
    );
  }
  if (route.kind === 'opps-list') {
    return (
      <ErrorBoundary label={`OpportunitiesList · ${route.stage}`}>
        <OpportunitiesList session={session} stage={route.stage} filtro={filtro} locationId={locationId} />
      </ErrorBoundary>
    );
  }
  if (route.kind === 'feedback') {
    return (
      <ErrorBoundary label="FeedbackChat">
        <Suspense fallback={<CargandoPantalla />}><FeedbackChat session={session} chatId={route.chatId} /></Suspense>
      </ErrorBoundary>
    );
  }
  if (route.kind === 'johny') {
    return (
      <ErrorBoundary label="JohnnyChat">
        <Suspense fallback={<CargandoPantalla />}><JohnnyChat session={session} chatId={route.chatId} /></Suspense>
      </ErrorBoundary>
    );
  }
  if (route.kind === 'onboarding') {
    return (
      <ErrorBoundary label="Onboarding">
        <Suspense fallback={<CargandoPantalla />}><Onboarding session={session} /></Suspense>
      </ErrorBoundary>
    );
  }
  return null;
}

/** "No se pudo cargar el panel" (o, desde la vista de agencia, como entrar en una subcuenta). Las pestañas, si las hay, encima. */
function PaginaDeError({ error, pestanas = null }: { error?: string; pestanas?: React.ReactNode }) {
  const isAgencyView = /unknown_location|user_not_in_client/.test(error ?? '') && /agency/i.test(error ?? '');
  if (isAgencyView) {
    return (
      <Page>
        {pestanas}
        <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: 20 }}>
          <h2 style={{ margin: '0 0 8px', color: '#92400e' }}>Abre el panel desde una subcuenta</h2>
          <p style={{ margin: '0 0 12px', color: '#78350f' }}>
            Estás viendo Fluxo Dashboard desde la vista de agencia. Las métricas son por subcuenta y por usuario, así que necesitas entrar primero en la subcuenta del cliente.
          </p>
          <p style={{ margin: 0, color: '#78350f', fontSize: 14 }}>
            <strong>Cómo:</strong> arriba a la izquierda en GHL, haz clic en el desplegable de cuenta y selecciona la subcuenta (ej. Actual Talent). Después haz clic otra vez en "Fluxo Dashboard".
          </p>
        </div>
      </Page>
    );
  }
  return (
    <Page>
      {pestanas}
      <h1 style={{ color: '#b91c1c' }}>No se pudo cargar el panel</h1>
      <p style={{ fontSize: 13, color: '#666' }}>{error}</p>
    </Page>
  );
}

function Page(props: { children: React.ReactNode }) {
  return <div className="shell">{props.children}</div>;
}
